import type { NavNode } from './parseMkdocsNav';

/**
 * The page path inside the legacy docs site, in the form of a nav item path:
 * `''` for the site home, no slash at the start or the end. `undefined` when
 * `pathname` is not a page of the site at `basePath`.
 */
export function getLegacyPagePath(
  pathname: string,
  basePath: string,
): string | undefined {
  // Entity refs ignore case, so a link with `Component` opens the same site.
  const lowerPathname = pathname.toLowerCase();
  const lowerBasePath = basePath.toLowerCase();
  if (
    lowerPathname !== lowerBasePath &&
    !lowerPathname.startsWith(`${lowerBasePath}/`)
  ) {
    return undefined;
  }

  return pathname
    .slice(basePath.length)
    .replace(/index\.html$/, '')
    .replace(/^\/+|\/+$/g, '');
}

/**
 * The nav items from the top of the tree down to the page with this exact
 * path: the groups above the page, then the page. `undefined` when the nav
 * has no such page.
 */
export function findNavTrail(
  nav: NavNode[],
  pagePath: string,
): NavNode[] | undefined {
  for (const node of nav) {
    if (node.path === pagePath) {
      return [node];
    }
    const trail = node.children && findNavTrail(node.children, pagePath);
    if (trail) {
      return [node, ...trail];
    }
  }
  return undefined;
}

/**
 * The page that a nav item links to: its own page, else the first page below
 * it. mkdocs groups often have no page of their own.
 */
export function getFirstPagePath(node: NavNode): string | undefined {
  if (node.path !== undefined) {
    return node.path;
  }
  for (const child of node.children ?? []) {
    const path = getFirstPagePath(child);
    if (path !== undefined) {
      return path;
    }
  }
  return undefined;
}
